import { useId, useState } from 'react';

/**
 * Small charts drawn as SVG from the design tokens (no chart library). Every chart takes the
 * same shape of data and a text alternative: the figures are always readable without the picture.
 */

export interface Series {
  label: string;
  values: number[];
  /** A token name from tokens.ts, e.g. 'chart-1'. */
  color?: string;
}

const W = 600;

function scale(values: number[], h: number, pad = 6) {
  const max = Math.max(1, ...values);
  const min = Math.min(0, ...values);
  return (v: number) => h - pad - ((v - min) / (max - min || 1)) * (h - pad * 2);
}

/** A tiny line for a KPI card: trend only, no axes. */
export function Sparkline({ values, color = 'chart-1', label }: { values: number[]; color?: string; label: string }) {
  const id = useId();
  if (values.length < 2) return null;
  const h = 36;
  const y = scale(values, h, 3);
  const x = (i: number) => (i / (values.length - 1)) * 120;
  const d = values.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  return (
    <svg className="sparkline" viewBox={`0 0 120 ${h}`} preserveAspectRatio="none" role="img" aria-label={label}>
      <defs>
        <linearGradient id={id} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor={`var(--${color})`} stopOpacity="0.22" />
          <stop offset="1" stopColor={`var(--${color})`} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={`${d} L120,${h} L0,${h} Z`} fill={`url(#${id})`} />
      <path d={d} fill="none" stroke={`var(--${color})`} strokeWidth="2" vectorEffect="non-scaling-stroke" strokeLinejoin="round" />
    </svg>
  );
}

/** Lines (or filled areas) over labelled points, with a hover read-out. */
export function LineChart({
  labels,
  series,
  area,
  height = 220,
  format = String,
  label,
}: {
  labels: string[];
  series: Series[];
  area?: boolean;
  height?: number;
  format?: (n: number) => string;
  label: string;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const gid = useId();
  const all = series.flatMap((s) => s.values);
  const y = scale(all, height, 12);
  const n = Math.max(2, labels.length);
  const x = (i: number) => (i / (n - 1)) * W;
  const max = Math.max(1, ...all);
  return (
    <figure className="chart">
      <svg
        viewBox={`0 0 ${W} ${height}`}
        preserveAspectRatio="none"
        className="chart-svg"
        style={{ height }}
        role="img"
        aria-label={label}
        onMouseLeave={() => setHover(null)}
        onMouseMove={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          setHover(Math.round(((e.clientX - r.left) / r.width) * (n - 1)));
        }}
      >
        {[0.25, 0.5, 0.75, 1].map((f) => (
          <line key={f} className="chart-grid" x1="0" x2={W} y1={y(max * f)} y2={y(max * f)} vectorEffect="non-scaling-stroke" />
        ))}
        {series.map((s, si) => {
          const c = `var(--${s.color ?? `chart-${si + 1}`})`;
          const d = s.values.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
          return (
            <g key={s.label}>
              {area ? (
                <>
                  <defs>
                    <linearGradient id={`${gid}-${si}`} x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0" stopColor={c} stopOpacity="0.2" />
                      <stop offset="1" stopColor={c} stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  <path d={`${d} L${W},${height} L0,${height} Z`} fill={`url(#${gid}-${si})`} />
                </>
              ) : null}
              <path d={d} fill="none" stroke={c} strokeWidth="2.25" vectorEffect="non-scaling-stroke" strokeLinejoin="round" strokeLinecap="round" />
            </g>
          );
        })}
        {hover !== null ? <line className="chart-cursor" x1={x(hover)} x2={x(hover)} y1="0" y2={height} vectorEffect="non-scaling-stroke" /> : null}
      </svg>
      <div className="chart-axis" aria-hidden="true">
        <span>{labels[0]}</span>
        <span>{labels[Math.floor((labels.length - 1) / 2)]}</span>
        <span>{labels[labels.length - 1]}</span>
      </div>
      {hover !== null && labels[hover] !== undefined ? (
        <div className="chart-tip" style={{ left: `${(hover / (n - 1)) * 100}%` }} aria-hidden="true">
          <strong>{labels[hover]}</strong>
          {series.map((s, si) => (
            <span key={s.label}>
              <i style={{ background: `var(--${s.color ?? `chart-${si + 1}`})` }} />
              {s.label}: {format(s.values[hover] ?? 0)}
            </span>
          ))}
        </div>
      ) : null}
      <Legend series={series} />
    </figure>
  );
}

/** Vertical bars; with two series they stand side by side (e.g. this year and last year). */
export function BarChart({ labels, series, height = 220, format = String, label }: { labels: string[]; series: Series[]; height?: number; format?: (n: number) => string; label: string }) {
  const [hover, setHover] = useState<number | null>(null);
  const all = series.flatMap((s) => s.values);
  const max = Math.max(1, ...all);
  const slot = W / Math.max(1, labels.length);
  const bw = Math.min(28, (slot * 0.7) / series.length);
  return (
    <figure className="chart">
      <svg viewBox={`0 0 ${W} ${height}`} preserveAspectRatio="none" className="chart-svg" style={{ height }} role="img" aria-label={label} onMouseLeave={() => setHover(null)}>
        {[0.25, 0.5, 0.75, 1].map((f) => (
          <line key={f} className="chart-grid" x1="0" x2={W} y1={height - 4 - (height - 16) * f} y2={height - 4 - (height - 16) * f} vectorEffect="non-scaling-stroke" />
        ))}
        {labels.map((_, i) =>
          series.map((s, si) => {
            const v = s.values[i] ?? 0;
            const bh = ((height - 16) * Math.max(0, v)) / max;
            const x0 = i * slot + (slot - bw * series.length) / 2 + si * bw;
            return (
              <rect
                key={`${i}-${si}`}
                x={x0}
                y={height - 4 - bh}
                width={bw - 2}
                height={bh}
                rx="3"
                fill={`var(--${s.color ?? `chart-${si + 1}`})`}
                opacity={hover === null || hover === i ? 1 : 0.45}
                onMouseEnter={() => setHover(i)}
              />
            );
          }),
        )}
      </svg>
      <div className="chart-axis" aria-hidden="true">
        <span>{labels[0]}</span>
        <span>{labels[Math.floor((labels.length - 1) / 2)]}</span>
        <span>{labels[labels.length - 1]}</span>
      </div>
      {hover !== null ? (
        <div className="chart-tip" style={{ left: `${((hover + 0.5) / labels.length) * 100}%` }} aria-hidden="true">
          <strong>{labels[hover]}</strong>
          {series.map((s, si) => (
            <span key={s.label}>
              <i style={{ background: `var(--${s.color ?? `chart-${si + 1}`})` }} />
              {s.label}: {format(s.values[hover] ?? 0)}
            </span>
          ))}
        </div>
      ) : null}
      <Legend series={series} />
    </figure>
  );
}

/** Parts of a whole, with the total in the middle and the parts listed beside it. */
export function DonutChart({
  parts,
  total,
  centre,
  format = String,
  label,
}: {
  parts: { label: string; value: number; color?: string }[];
  total?: string;
  centre?: string;
  format?: (n: number) => string;
  label: string;
}) {
  const sum = parts.reduce((a, p) => a + Math.max(0, p.value), 0);
  const r = 42;
  const c = 2 * Math.PI * r;
  let at = 0;
  return (
    <figure className="donut">
      <svg viewBox="0 0 100 100" role="img" aria-label={label}>
        <circle cx="50" cy="50" r={r} className="donut-track" />
        {sum > 0
          ? parts.map((p, i) => {
              const len = (Math.max(0, p.value) / sum) * c;
              const el = (
                <circle
                  key={p.label}
                  cx="50"
                  cy="50"
                  r={r}
                  fill="none"
                  stroke={`var(--${p.color ?? `chart-${i + 1}`})`}
                  strokeWidth="11"
                  strokeDasharray={`${Math.max(0, len - 1.2)} ${c}`}
                  strokeDashoffset={-at}
                  transform="rotate(-90 50 50)"
                />
              );
              at += len;
              return el;
            })
          : null}
        {total ? (
          <text x="50" y={centre ? 49 : 54} textAnchor="middle" className="donut-total" style={total.length > 7 ? { fontSize: `${Math.max(7, Math.round(96 / total.length))}px` } : undefined}>
            {total}
          </text>
        ) : null}
        {centre ? (
          <text x="50" y="61" textAnchor="middle" className="donut-centre">
            {centre}
          </text>
        ) : null}
      </svg>
      <ul className="donut-legend">
        {parts.map((p, i) => (
          <li key={p.label}>
            <i style={{ background: `var(--${p.color ?? `chart-${i + 1}`})` }} />
            <span>{p.label}</span>
            <strong>{format(p.value)}</strong>
          </li>
        ))}
      </ul>
    </figure>
  );
}

function Legend({ series }: { series: Series[] }) {
  if (series.length < 2) return null;
  return (
    <figcaption className="chart-legend">
      {series.map((s, si) => (
        <span key={s.label}>
          <i style={{ background: `var(--${s.color ?? `chart-${si + 1}`})` }} />
          {s.label}
        </span>
      ))}
    </figcaption>
  );
}
